import React, { useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import {
    InputSuffix,
    NumericTextBox as KendoNumericTextBox,
    NumericTextBoxChangeEvent,
    NumericTextBoxHandle,
    NumericTextBoxProps as KendoNumericTextBoxProps,
} from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { xIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import styles from '@components-clean/core/drag-drop-lists-core/style.module.scss';
import { useReadOnlyContext } from '@contexts-clean/read-only-context';
import { rangeNumberValidate } from '@utils-clean/validation';
import ValidationIconSuffix from 'views-clean/components/core/icons/validation-icon-suffix';

interface NumericTextBoxProps extends KendoNumericTextBoxProps {
    'data-testid': string;
    inline?: boolean;
}

const NumericTextBox = ({
    children,
    placeholder,
    valid,
    validationMessage,
    label,
    inline,
    value,
    min,
    max,
    onChange,
    'data-testid': testId,
    readOnly,
    disabled,
    ...props
}: NumericTextBoxProps) => {
    const { t } = useTranslation();
    const ref = useRef<NumericTextBoxHandle>(null);
    const hasMinOrMax = min !== undefined || max !== undefined;
    const [internalErrorMessage, setInternalErrorMessage] = useState<string | null>(null);
    const { readOnly: readonlyCtx } = useReadOnlyContext();

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', testId);
        if (ref.current?.element) {
            ref.current.element.onkeydown = (ev) => {
                if (min !== undefined && min >= 0 && ev.key === '-') {
                    ev.preventDefault();
                }
            };
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const minMaxErrorMessageDefine = () => {
        if (min !== undefined && max !== undefined) {
            return t('common:rangeNumberError', { min, max });
        }
        if (max !== undefined) {
            return t('common:numberLessThenError', { max });
        }
        if (min !== undefined) {
            return t('common:numberGreaterThanError', { min });
        }
        return null;
    };

    useEffect(() => {
        let isRangeInvalid = false;
        if ((value === 0 || value) && hasMinOrMax) {
            isRangeInvalid = rangeNumberValidate(value, min, max);
            const rangeErrorMessage = !isRangeInvalid ? minMaxErrorMessageDefine() : null;
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setInternalErrorMessage(rangeErrorMessage);
        } else if (value == null && hasMinOrMax) setInternalErrorMessage(null);
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [value]);

    const errorMessage = validationMessage || internalErrorMessage;

    return (
        <div
            className={cn({
                'k-flex-grow': !props.style?.width,
                'k-d-flex k-gap-4 k-align-items-center': inline,
            })}
        >
            <Label editorRef={ref} className={cn(inline ? ['!k-pb-0', 'k-white-space-nowrap'] : null)}>
                {label}
            </Label>
            <KendoNumericTextBox
                {...props}
                ref={ref}
                placeholder={!disabled ? (placeholder ?? t('cm2SelectValue')) : ''}
                valid={!valid ? valid : !errorMessage}
                onChange={onChange}
                value={value}
                validationMessage={errorMessage ?? ''}
                readOnly={readOnly || readonlyCtx}
                disabled={disabled}
                suffix={() => (
                    <div className="k-display-flex k-align-items-center">
                        {!!errorMessage && !readOnly && (
                            <ValidationIconSuffix validationMessage={errorMessage} padding="small" />
                        )}
                        {!!value && !readOnly && !readonlyCtx && (
                            <InputSuffix
                                onClick={() => {
                                    if (onChange) {
                                        onChange({ target: { value: null } } as NumericTextBoxChangeEvent);
                                    }
                                }}
                            >
                                <SvgIcon icon={xIcon} size="large" className={cn(styles.svgClear, '!k-pl-0')} />
                            </InputSuffix>
                        )}
                    </div>
                )}
            />
        </div>
    );
};

interface NumericTextBoxControllerProps<TData, TControl extends FieldValues> extends NumericTextBoxProps {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (event: NumericTextBoxChangeEvent) => void;
}

const NumericTextBoxController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled = false,
    'data-testid': dataTestId,
    label,
    min,
    max,
    format,
    required = false,
    readOnly = false,
    fillMode = 'solid',
    validateRules,
    handleInputChange,
    validationMessage,
    ...props
}: NumericTextBoxControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    rangeNumberValidate: (fieldValue) => {
                        if (!required && fieldValue == null) return true;
                        if (typeof min !== 'undefined' || typeof max !== 'undefined') {
                            return rangeNumberValidate(fieldValue as number | undefined, min, max);
                        }
                        return true;
                    },
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value: fieldValue, name: fieldName }, fieldState: { error } }) => {
                return (
                    <NumericTextBox
                        {...props}
                        name={fieldName}
                        data-testid={dataTestId}
                        label={label}
                        disabled={disabled}
                        valid={!error}
                        value={fieldValue as number | null}
                        min={min}
                        max={max}
                        format={format}
                        validationMessage={validationMessage && error ? validationMessage : error?.message}
                        onChange={(event) => {
                            onChange(event.target.value);
                            if (handleInputChange) handleInputChange(event);
                        }}
                        readOnly={readOnly}
                        fillMode={fillMode}
                    />
                );
            }}
        />
    );
};

export { NumericTextBox, NumericTextBoxController };
